import React, { useEffect, useState, useMemo, createRef } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/HighlightOff';
import LinkIcon from '@mui/icons-material/Link';
import Select from 'components/form/Select';
import Switch from 'components/form/Switch';
import IconButton from 'components/IconButton';
import Spin from 'components/Spin';
import { getGroups, updateGroup } from 'services/groups.service';
import { useSelector } from 'react-redux';
import CompletionNotificationSettings from './GroupFormCompletionSettings';
import GroupKeywordSettings from './GroupKeywordSettings';
import StaffNotificationSettings from './StaffNotificationSettings2';
import { useTranslation } from 'react-i18next';
import { saveConnectCard } from 'services/connect-card.service';
import useRequest from 'hooks/useRequest';

const GroupSummaryCard = ({ group, onRemove }) => {
  const [t] = useTranslation('groups');
  const [data, { loading }] = useRequest(getGroups, {
    hash: group.hash,
    load_counts: 1,
  });

  return (
    <Paper variant="outlined" sx={{ position: 'relative', mt: 2 }}>
      <Box p={2}>
        <Stack direction="row" sx={{ position: 'absolute', top: 1, right: 1 }}>
          <IconButton
            title="View Group"
            size="small"
            color="primary"
            target="_blank"
            to={`/groups/${group.hash}`}
            icon={<LinkIcon />}
          />
          <IconButton
            onClick={onRemove}
            title="Unselect Group"
            size="small"
            color="error"
            icon={<DeleteIcon />}
          />
        </Stack>
        <Typography gutterBottom>{group.group_name}</Typography>
        <Spin spinning={loading}>
          <Stack
            direction="row"
            spacing={3}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1 }}
            >
              {group.group_comments}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Typography variant="h2" fontWeight={600}>
                {data?.[0]?.member_count}
              </Typography>
              <Typography color="text.secondary">
                {t('members', 'Members')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography variant="h2" fontWeight={600}>
                {data?.[0]?.workflow_count}
              </Typography>
              <Typography color="text.secondary" whiteSpace="nowrap">
                {t('automated-workflows', 'Automated Workflows')}
              </Typography>
            </Stack>
          </Stack>
        </Spin>
      </Box>
    </Paper>
  );
};

const GroupSettings = ({ data, onChange }) => {
  const [t] = useTranslation('groups');
  const account = useSelector((state) => state.settings.account);
  const [groups, setGroups] = useState([]);
  const [values, setValues] = useState({});
  const [enableGroup, setEnableGroup] = useState(
    data.group_id && data.group_id !== '0',
  );
  const isGroupSelected = !!values.hash;

  const group = data.group_id
    ? groups.find((g) => g.value === data.group_id)
    : null;

  useEffect(() => {
    getGroups({}).then((resp) => {
      setGroups(
        resp.map((g) => ({
          label: g.group_name,
          value: g.group_id,
          ...g,
        })),
      );
    });
  }, []);

  useEffect(() => {
    if (group) {
      setValues(group);
    }
  }, [group]);

  const onUpdateGroup = async (update) => {
    return updateGroup(group.hash, update).then((resp) => {
      setValues(resp);
    });
  };

  const onSelectChange = (value) => {
    onChange({
      target: {
        name: 'group_id',
        value: value ? value.value : null,
      },
    });
    setValues({});
  };

  const onInputChange = (e) => {
    const { name, value, checked } = e.target;
    if (checked !== undefined) {
      setValues((values) => ({
        ...values,
        [name]: checked ? '1' : '0',
      }));
    } else {
      setValues((values) => ({
        ...values,
        [name]: value,
      }));
    }
  };

  const onToggleGroupEnabled = (e) => {
    const { checked } = e.target;
    setEnableGroup(checked);
    if (isGroupSelected) {
      onChange({
        target: {
          name: 'group_id',
          value: null,
        },
      });
    }
  };

  const onGroupSelect = (selected) => {
    saveConnectCard({
      hash: data.hash,
      group_id: selected.group_id,
    });
    onSelectChange(selected);
  };

  const onRemoveGroup = () => {
    saveConnectCard({
      hash: data.hash,
      group_id: null,
    });
    onSelectChange(null);
  };

  return (
    <>
      <Paper sx={{ mb: 3 }} elevation={6}>
        <Box p={3}>
          <Switch
            name="group_id"
            checked={enableGroup}
            onChange={onToggleGroupEnabled}
            margin="none"
            label={t(
              'link-connect-card-to-group',
              'Link Connect Card to Group (Optional)',
            )}
            hint="Add the person to the selected group when they complete your Connect Card ."
          />
          {enableGroup && !group && (
            <Select
              sx={{ display: 'block' }}
              isMulti={false}
              isLoading={!groups.length}
              placeholder="Select a group"
              value={values}
              options={groups}
              onChange={onGroupSelect}
              defaultOptions
              menuPosition="fixed"
            />
          )}
          {enableGroup && isGroupSelected && (
            <GroupSummaryCard group={values} onRemove={onRemoveGroup} />
          )}
        </Box>
      </Paper>
      {isGroupSelected && (
        <GroupKeywordSettings
          onUpdateGroup={onUpdateGroup}
          group={values}
          account={account}
          onInputChange={onInputChange}
        />
      )}
      {isGroupSelected && (
        <CompletionNotificationSettings
          onUpdateGroup={onUpdateGroup}
          group={values}
          onInputChange={onInputChange}
          setValues={setValues}
        />
      )}
      {isGroupSelected && (
        <StaffNotificationSettings
          onUpdateGroup={onUpdateGroup}
          card={data}
          group={values}
          setValues={setValues}
          onGroupChange={onInputChange}
        />
      )}
    </>
  );
};

export default GroupSettings;
